/* eslint-disable react/jsx-pascal-case */

import React, { useContext } from 'react'
import { shape, string, array, node, oneOfType } from 'prop-types'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { themeGet } from '@styled-system/theme-get'
import { Box, Button, Flex, Link, Text } from 'pcln-design-system'
import { WebPBackgroundImage } from '@pcln/content-tile'
import { usePartner } from '@pcln/whitelabel-components'
import analytics from '../../utils/analytics'
import { SetiRedesignContext } from '../providers/RedesignProvider'
import {
  ANALYTICS_CATEGORY_FLIGHTS,
  ANALYTICS_CATEGORY_HOTELS,
  ANALYTICS_CATEGORY_PACKAGES,
  ANALYTICS_CATEGORY_HOME
} from '../../constants/analytics'

const BannerWrapper = styled(Box)`
  border-bottom: ${props =>
    props.isHPCornerShadowColorRedesign
      ? ''
      : `1px solid ${props.theme.colors.borderGray}`};
`

const FlexDesktopOnly = styled(Flex)`
  display: none;
  ${themeGet('mediaQueries.lg')} {
    display: flex;
    flex: 1;
  }
`

const StyledWebPImage = styled(WebPBackgroundImage)`
  visibility: visible;
  background-position: ${props => props.imagePosition};
  background-color: transparent;
  background-size: ${props => props.imageSize};
  flex: 1;
`

const WrapperFlex = styled(Flex)`
  margin: 0 auto;
  max-width: 1280px;
`

const gaCategories = {
  '/flights': ANALYTICS_CATEGORY_FLIGHTS,
  '/hotels': ANALYTICS_CATEGORY_HOTELS,
  '/vacationpackages': ANALYTICS_CATEGORY_PACKAGES,
  '/': ANALYTICS_CATEGORY_HOME
}

const GenericBanner = props => {
  const router = useRouter()
  const pathname = router?.pathname
  const gaCategory = gaCategories[pathname] || ''
  const {
    content,
    contentFontColor,
    headingFontColor,
    smallHeadingFontColor,
    backgroundColor,
    imagePosition
  } = props
  const {
    buttons,
    contentText,
    gaAction,
    headingText,
    smallHeadingText,
    image,
    imageWebP,
    imageSize
  } = content

  const { isHPTextVariant } = useContext(SetiRedesignContext)
  const headingTextStyle = isHPTextVariant
    ? {
        fontSize: '28px',
        lineHeight: '36px'
      }
    : {
        fontSize: 5
      }
  const contentStyle = isHPTextVariant
    ? {
        fontSize: 2,
        lineHeight: '25px',
        color: 'text.dark'
      }
    : {
        fontSize: 3,
        color: contentFontColor
      }
  const partner = usePartner()
  const adBannerButtons =
    buttons &&
    buttons.map(button => {
      return (
        <Link
          key={`${button.gaLabel} Button`}
          href={button.linkUrl}
          target="_blank"
          rel="noopener"
          aria-label={`${button.gaLabel} Promo link`}
          onClick={() => {
            if (typeof partner === 'undefined') {
              analytics.fireEvent({
                action: gaAction,
                category: gaCategory,
                label: button.gaLabel
              })
            }
          }}
        >
          <Button data-testid={`${button.gaLabel}-button`}>
            {button.buttonText}
          </Button>
        </Link>
      )
    })
  const { isHPCornerShadowColorRedesign } = useContext(SetiRedesignContext)
  const redesignBannerStyle = isHPCornerShadowColorRedesign
    ? {
        px: 4,
        mx: 'auto',
        maxWidth: '1232px',
        borderRadius: 'xl',
        boxShadowSize: 'sm'
      }
    : { px: 3 }
  return (
    <BannerWrapper
      width={1}
      py={4}
      color={backgroundColor}
      isHPCornerShadowColorRedesign={isHPCornerShadowColorRedesign}
      {...redesignBannerStyle}
    >
      <WrapperFlex flexWrap="wrap">
        <Box
          my={[3, null, null, 5]}
          px={1}
          textAlign={['center', null, null, 'left']}
          width={[1, null, null, 2 / 5]}
        >
          <Box mb={3}>
            <Text
              caps
              color={smallHeadingFontColor}
              fontSize={0}
              fontWeight="bold"
              textStyle="overline"
              mb={3}
            >
              {smallHeadingText}
            </Text>
            <Text
              color={headingFontColor}
              {...headingTextStyle}
              fontWeight="bold"
              mb={3}
            >
              {headingText}
            </Text>
            <Text.span fontSize={3} {...contentStyle} mb={2}>
              {contentText}
            </Text.span>
          </Box>
          {adBannerButtons}
        </Box>
        <FlexDesktopOnly
          flexDirection="column"
          flexWrap="wrap"
          px={1}
          width={[1, null, null, 3 / 5]}
        >
          <StyledWebPImage
            image={image}
            imageWebP={imageWebP}
            borderRadius="xl"
            imageSize={imageSize}
            height="100%"
            imagePosition={imagePosition}
          />
        </FlexDesktopOnly>
      </WrapperFlex>
    </BannerWrapper>
  )
}

GenericBanner.defaultProps = {
  contentFontColor: 'text.light',
  headingFontColor: 'text.base',
  smallHeadingFontColor: 'text.light',
  backgroundColor: 'background.light',
  imagePosition: 'right'
}

GenericBanner.propTypes = {
  content: shape({
    gaAction: string,
    smallHeadingText: string,
    headingText: oneOfType([string, node]),
    contentText: oneOfType([string, node]),
    buttons: array,
    image: string,
    imageWebP: string,
    imageSize: string
  }).isRequired,
  children: node,
  contentFontColor: string,
  headingFontColor: string,
  smallHeadingFontColor: string,
  backgroundColor: string,
  imagePosition: string
}

export default GenericBanner
